import { Button } from '@unpublished/common-components'
import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ToggleButtonContainer = styled.div<{
  columnWidth: number
}>`
  width: 100%;
  margin-top: 16px;
  padding-inline-start: 1em;
  ${({ columnWidth }) => (columnWidth ? `column-width:${columnWidth}px;` : '')}
`

const ButtonDiv = styled.div`
  width: max-content;
`
const ToggleButton = styled(Button)<{
  selected: boolean
}>`
  ${({ selected }) =>
    selected
      ? `
    background : rgba(204, 204, 204, 0.7);
    > span:before {
        content: "\\2713\\00A0"; 
    }
    `
      : 'background : rgba(204, 204, 204, 0.15);'}
`

const StyledButton = styled.button`
  padding: 0.5em;
  cursor: pointer;
  background: none;
  color: Gray;
  border: none;
  font-size: 14px;
  cursor: pointer;
  outline: inherit;
  text-decoration: underline;
  &:hover {
    color: #303030;
    text-decoration: none;
  }
  &:disabled {
    text-decoration: none;
    cursor: default;
    color: Gray;
  }
`

export interface Option<ValueType> {
  label: string
  value: ValueType
}

// To improve rendering performance, the caller should memoize `options`.
export function ToggleableButtonList<ValueType>({
  options,
  onChange,
  selectedOptionValues,
  showSelectAllNoneButtons = true,
  direction = 'column',
  disabled = false,
}: {
  options: Option<ValueType>[]
  onChange: (optionValues: Set<ValueType>) => void
  selectedOptionValues: Set<ValueType>
  showSelectAllNoneButtons?: boolean
  direction?: 'row' | 'column'
  disabled?: boolean
}): JSX.Element {
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const [widthOfWidestChild, setWidthOfWidestChild] = useState(0)

  function toggleSelectedOptions(value: ValueType): void {
    const newSelectedOptions = new Set(selectedOptionValues)
    if (newSelectedOptions.has(value)) {
      newSelectedOptions.delete(value)
    } else {
      newSelectedOptions.add(value)
    }
    onChange(newSelectedOptions)
  }

  function selectAllOptions(): void {
    onChange(new Set(Array.from(options).map(s => s.value)))
  }

  function deselectAllOptions(): void {
    onChange(new Set())
  }

  // List must be initially rendered to surface the element widths
  useLayoutEffect(() => {
    if (buttonContainerRef.current) {
      const buttonWidths = Array.from(buttonContainerRef.current.children).map(
        // `child.firstChild` is the <button> inside the <li>.
        child => (child.firstChild as HTMLButtonElement)?.offsetWidth
      )
      setWidthOfWidestChild(Math.max(...buttonWidths))
    }
  }, [buttonContainerRef, options])

  if (new Set(options.map(option => option.label)).size !== options.length) {
    throw Error('Option labels must be unique')
  }

  return (
    <div>
      <ToggleButtonContainer
        ref={buttonContainerRef}
        columnWidth={widthOfWidestChild}
      >
        {options.map(option => (
          <ButtonDiv key={option.label}>
            <ToggleButton
              selected={selectedOptionValues?.has(option.value)}
              onClick={() => toggleSelectedOptions(option.value)}
              disabled={disabled}
            >
              <span>{option.label}</span>
            </ToggleButton>
          </ButtonDiv>
        ))}
      </ToggleButtonContainer>
      {showSelectAllNoneButtons && options.length > 1 ? (
        <div>
          <StyledButton onClick={selectAllOptions} disabled={disabled}>
            Select All
          </StyledButton>
          <StyledButton onClick={deselectAllOptions} disabled={disabled}>
            Select None
          </StyledButton>
        </div>
      ) : undefined}
    </div>
  )
}
